/**
 * Collapsible trader want lists (Send Cards and Activity). Display only: nothing
 * here touches packages, prices or points.
 */

/** Rows of cards a collapsed list shows in the image layouts. */
export const COLLAPSED_ROWS = 2;
/** Table rows a collapsed list shows in Send's List density. */
export const LIST_COLLAPSED_ROWS = 5;
/** A want this recent gets a NEW tag in the collapsed view. */
export const NEW_WANT_WINDOW_DAYS = 7;

export interface TraderListCollapsePrefs {
  collapseAll: boolean;
  /** traderUserId (stringified) -> collapsed. An entry overrides collapseAll. */
  overrides: Record<string, boolean>;
}

export const DEFAULT_TRADER_LIST_COLLAPSE_PREFS: TraderListCollapsePrefs = {
  collapseAll: false,
  overrides: {},
};

/** The trader's own override when one exists, otherwise the collapse-all setting. */
export const isTraderCollapsed = (
  prefs: TraderListCollapsePrefs | undefined | null,
  traderId: number | string,
): boolean => {
  if (!prefs) return false;
  const override = prefs.overrides?.[String(traderId)];
  return override !== undefined ? override : !!prefs.collapseAll;
};

/**
 * Collapsed-view order: newest want first, then the order the page already had.
 * The sort is only applied when every item carries a usable timestamp; with any
 * missing (a backend that does not send it yet) the input order is returned
 * untouched rather than guessed at.
 */
export function orderForCollapsed<T>(
  items: readonly T[],
  getWantCreatedAt: (item: T) => string | null | undefined,
): T[] {
  const times = items.map((item) => {
    const raw = getWantCreatedAt(item);
    return raw ? Date.parse(raw) : NaN;
  });
  if (times.some((t) => Number.isNaN(t))) return [...items];
  return items
    .map((item, index) => ({ item, index, time: times[index] }))
    .sort((a, b) => b.time - a.time || a.index - b.index)
    .map((entry) => entry.item);
}

/** True when the want was created inside the NEW window. False for a missing or future-less timestamp. */
export const isNewWant = (wantCreatedAt: string | null | undefined, now: number = Date.now()): boolean => {
  if (!wantCreatedAt) return false;
  const created = Date.parse(wantCreatedAt);
  if (Number.isNaN(created)) return false;
  return now - created <= NEW_WANT_WINDOW_DAYS * 24 * 60 * 60 * 1000;
};
